import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { API, useAuth } from '@/App';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, Loader2, Pencil, Trash2, ArrowRight, Phone, Mail, Upload, Download, FileText, Sparkles, Home, Repeat2, GitMerge } from 'lucide-react';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import ReferrerSelect from '@/components/ReferrerSelect';
import MergeIntoDialog from '@/components/MergeIntoDialog';

const PollenDotsCustomers = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', zoho_customer_id: '', referrer_id: '', referrer_type: 'direct' });
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [extractingFile, setExtractingFile] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [mergeSource, setMergeSource] = useState(null);
  // Segment filter — mirrors the dashboard KPI split so the Customers tab
  // can be sliced the same way operators think about them.
  //   all      → every record
  //   direct   → referrer_type === 'direct' OR no referrer_id stored
  //   referred → has a non-empty referrer_id (customer / team / personal_contact)
  //   repeat   → customer has ≥2 tours
  const [segment, setSegment] = useState('all');

  const authFetch = (url, opts = {}) => {
    const h = {}; const t = localStorage.getItem('session_token'); if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const fetchData = useCallback(async () => {
    try {
      const [cRes, tRes, tmRes] = await Promise.all([
        authFetch(`${POLLEN_API}/customers`),
        authFetch(`${POLLEN_API}/tours`),
        authFetch(`${POLLEN_API}/team`),
      ]);
      if (cRes.ok) {
        const custs = await cRes.json();
        const tours = tRes.ok ? await tRes.json() : [];
        const enriched = custs.map(c => {
          // Tours this customer was on — either as primary booker (customer_id)
          // or as a co-traveller in group_members. Mirrors the backend
          // `/customers/{cid}` profile endpoint so all surfaces agree on
          // "trips this person took". BOS / Invoiced / Booked / Planning all
          // count — status is never filtered.
          const cTours = tours.filter(t =>
            t.customer_id === c.id ||
            (t.group_members || []).some(gm => gm.customer_id === c.id)
          );
          // Lifetime revenue stays anchored to lead-booker tours so we don't
          // double-count a single family package across every member's row.
          const primaryTours = cTours.filter(t => t.customer_id === c.id);
          return {
            ...c,
            tour_count: cTours.length,
            lifetime_revenue: primaryTours.reduce((s, t) => s + (t.customer_amount || 0), 0),
          };
        });
        setCustomers(enriched);
      }
      if (tmRes.ok) setTeamMembers(await tmRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isDirect = (c) => !c.referrer_id || c.referrer_type === 'direct';
  const isReferred = (c) => !!c.referrer_id && c.referrer_type !== 'direct';
  const isRepeat = (c) => (c.tour_count || 0) >= 2;

  const segmentCounts = {
    all: customers.length,
    direct: customers.filter(isDirect).length,
    referred: customers.filter(isReferred).length,
    repeat: customers.filter(isRepeat).length,
  };

  const filtered = customers.filter(c => {
    // Segment filter first (cheap), then search.
    if (segment === 'direct' && !isDirect(c)) return false;
    if (segment === 'referred' && !isReferred(c)) return false;
    if (segment === 'repeat' && !isRepeat(c)) return false;
    const s = search.toLowerCase();
    return !s || c.name?.toLowerCase().includes(s) || c.phone?.includes(s) || c.email?.toLowerCase().includes(s);
  });

  // On the Repeat tab, sort loyal customers to the top by tour count (desc).
  // Preserves the default sort for every other tab.
  const displayedCustomers = segment === 'repeat'
    ? [...filtered].sort((a, b) => (b.tour_count || 0) - (a.tour_count || 0))
    : filtered;

  // Inline badge used on each repeat row to call out the loyalty count —
  // e.g. "× 7 tours". Only rendered when the Repeat tab is active so it
  // doesn't clutter the All/Direct/Referred views.
  const RepeatBadge = ({ count }) => (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200"
      data-testid="repeat-count-badge"
      title={`Has booked ${count} tours`}
    >
      <Repeat2 className="w-3 h-3" />× {count}
    </span>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = editMode ? `${POLLEN_API}/customers/${editId}` : `${POLLEN_API}/customers`;
      const res = await authFetch(url, { method: editMode ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) { toast.success(editMode ? 'Customer updated' : 'Customer added'); setDialogOpen(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error'); }
    setSubmitting(false);
  };

  const handleEdit = (c) => { setForm({ name: c.name, phone: c.phone || '', email: c.email || '', address: c.address || '', zoho_customer_id: c.zoho_customer_id || '', referrer_id: c.referrer_id || '', referrer_type: c.referrer_type || (c.referrer_id ? 'customer' : 'direct') }); setEditId(c.id); setEditMode(true); setDialogOpen(true); };
  const handleDelete = async (id) => { await authFetch(`${POLLEN_API}/customers/${id}`, { method: 'DELETE' }); toast.success('Deleted'); setDeleteId(null); fetchData(); };
  const openAdd = () => { setForm({ name: '', phone: '', email: '', address: '', zoho_customer_id: '', referrer_id: '', referrer_type: 'direct' }); setEditMode(false); setDialogOpen(true); };

  // Composite value format: "direct" | "personal_contact" | "customer:<id>" | "team_member:<id>"
  const parseReferrerComposite = (val) => {
    if (!val || val === 'direct') return { referrer_id: '', referrer_type: 'direct' };
    if (val === 'personal_contact') return { referrer_id: '', referrer_type: 'personal_contact' };
    const [type, id] = val.split(':');
    return { referrer_id: id || '', referrer_type: type };
  };

  const updateReferrer = async (customer, compositeVal) => {
    try {
      const { referrer_id, referrer_type } = parseReferrerComposite(compositeVal);
      const payload = {
        name: customer.name,
        phone: customer.phone || '',
        email: customer.email || '',
        address: customer.address || '',
        zoho_customer_id: customer.zoho_customer_id || '',
        referrer_id, referrer_type,
      };
      const res = await authFetch(`${POLLEN_API}/customers/${customer.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (res.ok) {
        setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, referrer_id, referrer_type } : c));
        toast.success(referrer_type === 'direct' ? 'Referrer cleared' : 'Referrer set');
      } else {
        toast.error('Failed to update referrer');
      }
    } catch (e) { toast.error('Error updating referrer'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await authFetch(`${POLLEN_API}/customers/import`, { method: 'POST', body: fd });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Imported ${result.created} new, ${result.updated || 0} updated, ${result.skipped} unchanged (${result.total_rows} total rows)`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Import failed');
      }
    } catch (e) { toast.error('Import error'); }
    setImporting(false);
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const csv = 'name,phone,email,address,zoho_customer_id\nJohn Doe,+91 9876543210,john@example.com,"123 Street, City",ZOHO001\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'customer_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExtractFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtractingFile(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await authFetch(`${POLLEN_API}/customers/extract-file`, { method: 'POST', body: fd });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Extracted ${result.created} new, ${result.matched || 0} matched existing (${result.updated || 0} updated) — ${result.total_found} found in file`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Extraction failed');
      }
    } catch (e) { toast.error('Extraction error'); }
    setExtractingFile(false);
    e.target.value = '';
  };

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  if (loading) return <Layout><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-4" data-testid="customers-list-page">
        <PollenDotsNav />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Customers</h1>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="w-4 h-4 mr-1" />Template</Button>
            <label className="cursor-pointer">
              <input type="file" className="hidden" accept=".csv,.xlsx,.xls" onChange={handleImport} data-testid="import-file-input" />
              <Button variant="outline" size="sm" asChild disabled={importing}>
                <span>{importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}{importing ? 'Importing...' : 'Import CSV/Excel'}</span>
              </Button>
            </label>
            <label className="cursor-pointer">
              <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.txt" onChange={handleExtractFile} data-testid="extract-file-input" />
              <Button variant="outline" size="sm" asChild disabled={extractingFile} className="border-amber-300 hover:bg-amber-50">
                <span>{extractingFile ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1 text-amber-600" />}{extractingFile ? 'Extracting...' : 'Extract from File'}</span>
              </Button>
            </label>
            <Button onClick={openAdd} data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-1" />Add Customer</Button>
          </div>
        </div>
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by name, phone, email..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="customer-search" />
        </div>
        <Tabs value={segment} onValueChange={setSegment} data-testid="customer-segment-tabs">
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="all" className="text-xs px-3 py-1.5" data-testid="seg-all">
              All <span className="ml-1.5 text-[10px] opacity-70">{segmentCounts.all}</span>
            </TabsTrigger>
            <TabsTrigger value="direct" className="text-xs px-3 py-1.5" data-testid="seg-direct">
              Direct <span className="ml-1.5 text-[10px] opacity-70">{segmentCounts.direct}</span>
            </TabsTrigger>
            <TabsTrigger value="referred" className="text-xs px-3 py-1.5" data-testid="seg-referred">
              Referred <span className="ml-1.5 text-[10px] opacity-70">{segmentCounts.referred}</span>
            </TabsTrigger>
            <TabsTrigger value="repeat" className="text-xs px-3 py-1.5" data-testid="seg-repeat">
              Repeat <span className="ml-1.5 text-[10px] opacity-70">{segmentCounts.repeat}</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {displayedCustomers.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No customers found</CardContent></Card>
        ) : (
          <>
            <div className="sm:hidden space-y-3 max-h-[70vh] overflow-y-auto pr-1" data-testid="customers-mobile-scroll">
              {displayedCustomers.map(c => (
                <Card key={c.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/pollen-dots/customer/${c.id}`)}>
                  <CardContent className="p-4 space-y-1">
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium truncate">{c.name}</span>
                        {segment === 'repeat' && <RepeatBadge count={c.tour_count || 0} />}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{c.tour_count} tours</span>
                    </div>
                    {c.phone && <div className="text-xs text-muted-foreground"><Phone className="w-3 h-3 inline mr-1" />{c.phone}</div>}
                    {c.email && <div className="text-xs text-muted-foreground"><Mail className="w-3 h-3 inline mr-1" />{c.email}</div>}
                    {c.address && <div className="text-xs text-muted-foreground"><Home className="w-3 h-3 inline mr-1" />{c.address}</div>}
                    <div onClick={e => e.stopPropagation()} className="pt-1">
                      <label className="text-[10px] text-muted-foreground">Referred by</label>
                      <ReferrerSelect
                        testId={`inline-referrer-mobile-${c.id}`}
                        size="sm"
                        value={{ referrer_type: c.referrer_type || (c.referrer_id ? 'customer' : 'direct'), referrer_id: c.referrer_id || '' }}
                        onChange={next => {
                          const composite = next.referrer_type === 'direct' ? 'direct'
                            : next.referrer_type === 'personal_contact' ? 'personal_contact'
                            : `${next.referrer_type}:${next.referrer_id}`;
                          updateReferrer(c, composite);
                        }}
                        teamMembers={teamMembers}
                        customers={customers}
                        excludeCustomerId={c.id}
                      />
                    </div>
                    <div className="flex justify-between pt-1">
                      <span className="text-xs text-muted-foreground">Revenue: {c.lifetime_revenue?.toLocaleString('en-IN')}</span>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={e => { e.stopPropagation(); handleEdit(c); }}><Pencil className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-amber-600" title="Merge duplicate" onClick={e => { e.stopPropagation(); setMergeSource(c); }} data-testid={`merge-customer-${c.id}`}><GitMerge className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={e => { e.stopPropagation(); setDeleteId(c.id); }}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="hidden sm:block overflow-x-auto max-h-[70vh] overflow-y-auto" data-testid="customers-desktop-scroll">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Email</TableHead><TableHead>Address</TableHead>
                  <TableHead>Referred By</TableHead>
                  <TableHead>Tours</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {displayedCustomers.map(c => (
                    <TableRow key={c.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/pollen-dots/customer/${c.id}`)}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{c.name}</span>
                          {segment === 'repeat' && <RepeatBadge count={c.tour_count || 0} />}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{c.phone || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{c.email || '-'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">{c.address || '-'}</TableCell>
                      <TableCell onClick={e => e.stopPropagation()} className="min-w-[220px]">
                        <ReferrerSelect
                          testId={`inline-referrer-${c.id}`}
                          size="sm"
                          value={{ referrer_type: c.referrer_type || (c.referrer_id ? 'customer' : 'direct'), referrer_id: c.referrer_id || '' }}
                          onChange={next => {
                            const composite = next.referrer_type === 'direct' ? 'direct'
                              : next.referrer_type === 'personal_contact' ? 'personal_contact'
                              : `${next.referrer_type}:${next.referrer_id}`;
                            updateReferrer(c, composite);
                          }}
                          teamMembers={teamMembers}
                          customers={customers}
                          excludeCustomerId={c.id}
                        />
                      </TableCell>
                      <TableCell>{c.tour_count}</TableCell>
                      <TableCell className="text-right font-medium">{c.lifetime_revenue?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleEdit(c); }}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-amber-600" title="Merge duplicate" onClick={e => { e.stopPropagation(); setMergeSource(c); }} data-testid={`merge-customer-row-${c.id}`}><GitMerge className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={e => { e.stopPropagation(); setDeleteId(c.id); }}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editMode ? 'Edit' : 'Add'} Customer</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="input-cust-name" /></div>
              <div><Label>Phone <span className="text-[10px] text-muted-foreground">(comma-separated for multiple)</span></Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="input-cust-phone" placeholder="+91 9317752915, +91 9805541209" /></div>
              <div><Label>Email <span className="text-[10px] text-muted-foreground">(comma-separated for multiple)</span></Label><Input type="text" inputMode="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="input-cust-email" /></div>
              <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <div><Label>Zoho Customer ID</Label><Input value={form.zoho_customer_id} onChange={e => setForm({...form, zoho_customer_id: e.target.value})} /></div>
              <div>
                <Label>Referred By</Label>
                <ReferrerSelect
                  testId="input-cust-referrer"
                  value={{ referrer_type: form.referrer_type, referrer_id: form.referrer_id }}
                  onChange={next => setForm({ ...form, referrer_type: next.referrer_type, referrer_id: next.referrer_id })}
                  teamMembers={teamMembers}
                  customers={customers}
                  excludeCustomerId={editId}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="submit-cust">{submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{editMode ? 'Update' : 'Add'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        <ConfirmDeleteDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => handleDelete(deleteId)} title="Delete Customer" description="This will permanently delete this customer. Are you sure?" />
        <MergeIntoDialog
          open={!!mergeSource}
          onOpenChange={(v) => !v && setMergeSource(null)}
          sourceItem={mergeSource}
          candidates={customers}
          endpoint="/pollen/customers/merge"
          entityLabel="customer"
          itemMeta={(c) => [c.phone, c.email, c.address].filter(Boolean).join(' \u00b7 ')}
          onMerged={fetchData}
        />
      </div>
    </Layout>
  );
};

export default PollenDotsCustomers;
